import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { getOptimizedImageUrl } from '../lib/utils';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const navItems = [
  {
    name: 'Fundamentløsninger',
    href: '/skruefundering',
    children: [
      { name: 'Skruefundering', href: '/skruefundering', desc: 'Professionel montering og dimensionering' },
      { name: 'Jordanker', href: '/jordanker', desc: 'Sikker forankring i alle jordtyper' },
      { name: 'GroundPlug', href: '/groundplug', desc: 'Hurtig og fleksibel funderingsløsning' },
    ],
  },
  {
    name: 'Skruer',
    href: '/skruer',
    children: [
      { name: 'Alle skruer', href: '/skruer', desc: 'Komplet produktoversigt og tekniske datablade' },
      { name: 'Terrasseskruer', href: '/terrasseskruer', desc: 'Skruer optimeret til træterrasser' },
      { name: 'Jordskruer', href: '/jordskruer', desc: 'Kraftige stålskruer til byggeri' },
    ],
  },
  {
    name: 'Projekter',
    href: '/projekter',
  },
  {
    name: 'Om os',
    href: '/om-os',
  },
  {
    name: 'Kontakt',
    href: '/kontakt',
  },
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const location = useLocation();
  const dropdownTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu and dropdowns when location changes
  useEffect(() => {
    setIsOpen(false);
    setOpenDesktopDropdown(null);
  }, [location]);

  const handleMouseEnter = (name) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setOpenDesktopDropdown(name);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDesktopDropdown(null);
    }, 150);
  };

  const toggleMobileSubmenu = (name) => {
    setMobileExpanded((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const isItemActive = (item) => {
    if (item.href && location.pathname === item.href) return true;
    if (item.children) {
      return item.children.some((child) => location.pathname === child.href);
    }
    return false;
  };

  const logoUrl = getOptimizedImageUrl(
    'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/34a78654-9cb6-4b5b-93fb-23a3c5578977.png',
    { width: 600, quality: 95 }
  );

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b',
          scrolled ? 'bg-white py-3 border-slate-200 shadow-sm' : 'bg-white/95 backdrop-blur-sm py-5 border-transparent'
        )}
      >
        <div className="max-w-[1500px] 2xl:max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="flex justify-between items-center gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0" aria-label="Gå til forsiden">
              <img
                src={logoUrl}
                alt="CM Skruefundament ApS – Professionel skruefundering og jordskruer i Danmark"
                title="CM Skruefundament ApS"
                className="w-[190px] xl:w-auto h-auto min-[1200px]:h-15 xl:h-16 object-contain hover:scale-105 transition-transform duration-300"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden min-[1200px]:flex items-center gap-5 xl:gap-7 shrink-0">
              {navItems.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const active = isItemActive(item);

                if (hasChildren) {
                  return (
                    <div
                      key={item.name}
                      className="relative group py-2"
                      onMouseEnter={() => handleMouseEnter(item.name)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {item.href ? (
                        <Link
                          to={item.href}
                          className={cn(
                            'flex items-center gap-1.5 text-sm font-bold uppercase tracking-widest transition-colors py-1 focus:outline-none cursor-pointer whitespace-nowrap',
                            active ? 'text-slate-900 border-b-2 border-orange-500 pb-0.5' : 'text-slate-600 hover:text-slate-900'
                          )}
                          aria-expanded={openDesktopDropdown === item.name}
                        >
                          <span className="whitespace-nowrap">{item.name}</span>
                          <ChevronDown
                            className={cn(
                              'w-4 h-4 transition-transform duration-200 shrink-0',
                              openDesktopDropdown === item.name ? 'rotate-180 text-orange-500' : 'text-slate-400'
                            )}
                          />
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className={cn(
                            'flex items-center gap-1.5 text-sm font-bold uppercase tracking-widest transition-colors py-1 focus:outline-none cursor-pointer whitespace-nowrap',
                            active ? 'text-slate-900 border-b-2 border-orange-500 pb-0.5' : 'text-slate-600 hover:text-slate-900'
                          )}
                          aria-expanded={openDesktopDropdown === item.name}
                        >
                          <span className="whitespace-nowrap">{item.name}</span>
                          <ChevronDown
                            className={cn(
                              'w-4 h-4 transition-transform duration-200 shrink-0',
                              openDesktopDropdown === item.name ? 'rotate-180 text-orange-500' : 'text-slate-400'
                            )}
                          />
                        </button>
                      )}

                      {/* Dropdown Menu */}
                      <div
                        className={cn(
                          'absolute top-full left-0 w-72 bg-white rounded-xl shadow-xl border border-slate-100 p-2.5 transition-all duration-200 origin-top-left',
                          openDesktopDropdown === item.name
                            ? 'opacity-100 scale-100 pointer-events-auto translate-y-1'
                            : 'opacity-0 scale-95 pointer-events-none translate-y-0'
                        )}
                      >
                        <div className="space-y-1">
                          {item.children.map((sub) => {
                            const subActive = location.pathname === sub.href;
                            return (
                              <Link
                                key={sub.name}
                                to={sub.href}
                                className={cn(
                                  'block px-3.5 py-2.5 rounded-lg transition-all',
                                  subActive
                                    ? 'bg-slate-900 text-white'
                                    : 'text-slate-700 hover:bg-slate-50 hover:text-orange-600'
                                )}
                              >
                                <div className="text-sm font-semibold">{sub.name}</div>
                                {sub.desc && (
                                  <div
                                    className={cn(
                                      'text-xs mt-0.5 leading-snug',
                                      subActive ? 'text-slate-300' : 'text-slate-400'
                                    )}
                                  >
                                    {sub.desc}
                                  </div>
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'text-sm font-bold uppercase tracking-widest transition-colors hover:text-slate-900 whitespace-nowrap shrink-0',
                      active ? 'text-slate-900 border-b-2 border-orange-500 pb-0.5' : 'text-slate-600'
                    )}
                  >
                    {item.name}
                  </Link>
                );
              })}

              <Link
                to="/kontakt"
                className="bg-slate-900 text-white px-5 xl:px-6 py-2.5 xl:py-3 text-sm font-bold uppercase tracking-widest hover:bg-orange-500 hover:shadow-lg transition-all rounded whitespace-nowrap shrink-0"
              >
                Få et tilbud
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <div className="min-[1200px]:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-slate-900 p-2 focus:outline-none"
                aria-label="Toggle menu"
              >
                {isOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Nav Sidebar */}
      <div
        className={cn(
          'fixed inset-0 z-[60] min-[1200px]:hidden transition-all duration-300 ease-in-out',
          isOpen ? 'translate-x-0 opacity-100 pointer-events-auto' : 'translate-x-full opacity-0 pointer-events-none'
        )}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsOpen(false)} />

        {/* Sidebar Content */}
        <div className="absolute right-0 top-0 bottom-0 w-[300px] sm:w-[340px] bg-[#0e162b] shadow-2xl flex flex-col p-6 overflow-y-auto">
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-slate-800">
            <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Menu</span>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white p-2 hover:text-orange-400 transition-colors"
              aria-label="Luk menu"
            >
              <X size={26} />
            </button>
          </div>

          <nav className="flex flex-col gap-3">
            {navItems.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const active = isItemActive(item);
              const isSubExpanded = mobileExpanded[item.name] ?? active;

              if (hasChildren) {
                return (
                  <div key={item.name} className="flex flex-col border-b border-slate-800/60 pb-3">
                    <div className="flex items-center justify-between w-full">
                      {item.href ? (
                        <Link
                          to={item.href}
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            'py-2 text-base font-bold uppercase tracking-wider transition-colors flex-1',
                            location.pathname === item.href ? 'text-orange-400' : 'text-white hover:text-slate-200'
                          )}
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <span className="py-2 text-base font-bold uppercase tracking-wider text-white flex-1">
                          {item.name}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleMobileSubmenu(item.name)}
                        className="p-2 text-slate-400 hover:text-white transition-colors focus:outline-none"
                        aria-label={`Udvid ${item.name} undermenu`}
                      >
                        <ChevronDown
                          className={cn(
                            'w-5 h-5 transition-transform duration-200',
                            isSubExpanded ? 'rotate-180 text-orange-400' : 'text-slate-400'
                          )}
                        />
                      </button>
                    </div>

                    {isSubExpanded && (
                      <div className="pl-3 mt-1 space-y-2 border-l-2 border-orange-500/40">
                        {item.children.map((sub) => (
                          <Link
                            key={sub.name}
                            to={sub.href}
                            onClick={() => setIsOpen(false)}
                            className={cn(
                              'block py-1.5 text-sm font-medium transition-colors',
                              location.pathname === sub.href
                                ? 'text-orange-400 font-bold'
                                : 'text-slate-300 hover:text-white'
                            )}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'py-2 text-base font-bold uppercase tracking-wider border-b border-slate-800/60 transition-colors',
                    active ? 'text-orange-400' : 'text-slate-200 hover:text-white'
                  )}
                >
                  {item.name}
                </Link>
              );
            })}

            <Link
              to="/kontakt"
              className="mt-6 bg-orange-500 text-white px-6 py-3.5 text-center text-sm font-bold uppercase tracking-widest hover:bg-orange-600 transition-colors rounded shadow-lg"
            >
              Få et tilbud
            </Link>
          </nav>
        </div>
      </div>
    </>
  );
}
